// src/components/Admin/AdminDashboard.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  db,
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  orderBy,
  auth
} from '../../firebase';
import { query, where, limit, or, and, getCountFromServer } from 'firebase/firestore';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import AdminSidebar from './AdminSidebar';
import AdminTopbar from './AdminTopbar';
import Loader from '../Loader';

import AdminDashboardContent from './AdminDashboardContent';

import CurriculumPlanPage from '../CurriculumPlan/CurriculumPlanPage';
import SchedulePage from './SchedulePage';

import {
  Users,
  GraduationCap,
  BriefcaseBusiness,
  FileText,
  BookOpen,
  Activity,
  Clock3,
  UserRound,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Camera,
  Save,
  X,
  Edit3,
  CalendarDays,
  CheckCircle2,
  FolderOpen,
  BarChart3,
  Award
} from 'lucide-react';

import './AdminDashboard.css';

const ADMIN_ID = 'admin_user';
const defaultAccess = {
  users: 'none',
  teachers: 'none',
  students: 'none',

  schedule: 'none',
  plans: 'none',
  programs: 'none',
  documents: 'none',
  teacherFiles: 'none',

  reports: 'none',
  reportsCurator: 'none',
  reportsDepartment: 'none',

  practices: 'none',
  achievements: 'none'
};

const formatActionDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('ru-RU');
};

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();

const normalizeComparableValue = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value).trim();
};

const removeUndefinedFields = (obj = {}) => {
  const cleaned = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  });

  return cleaned;
};

const monthNames = {
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  kg: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

const formatDate = (date) => {
  if (!(date instanceof Date)) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const normalizePositionToRole = (position = '', currentRole = 'teacher') => {
  const value = normalizeText(position);

  if (value.includes('лаборант') || value.includes('laborant') || value.includes('lab')) {
    return 'teacher';
  }

  if (value.includes('администратор') || value.includes('admin')) {
    return 'admin';
  }

  if (value.includes('преподаватель') || value.includes('teacher')) {
    return 'teacher';
  }

  return currentRole || 'teacher';
};

const normalizeUser = (id, data = {}) => {
  const entered =
    data.hasLoggedIn === true ||
    data.loginStatus === 'entered' ||
    Boolean(data.lastLoginAt || data.loginDate);

  const roleFromPosition =
    data.position && data.role !== 'student'
      ? normalizePositionToRole(data.position, data.role)
      : data.role;

  return {
    id,
    ...data,
    name: data.name || data.fullName || '',
    fullName: data.fullName || data.name || '',
    role: roleFromPosition || data.role || 'teacher',
    position: data.position || '',
    access: {
      ...defaultAccess,
      ...(data.access || {})
    },
    loginStatus: entered ? 'entered' : 'waiting',
    isRegistered: entered,
    hasLoggedIn: entered
  };
};

const firestoreSnapshotToArray = (snapshot) => {
  if (!snapshot || snapshot.empty) return [];

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...(docItem.data() || {})
  }));
};

function RecentActionsModal({ t, actions, onClose }) {
  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div
        className="admin-modal-window recent-actions-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-modal-head">
          <div>
            <h3>{t.allActions || t.recentActions || 'Все действия'}</h3>
            <p>{t.recentActions || 'Последние действия'}</p>
          </div>

          <button type="button" className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="admin-modal-body actions-full-list">
          {actions.length === 0 ? (
            <div className="empty-actions">
              {t.noActions || 'Действий нет'}
            </div>
          ) : (
            actions.map((item, index) => (
              <div className="action-full-item" key={item.id || index}>
                <span className="action-icon">{item.icon || '•'}</span>
                <div>
                  <strong>{item.text || item.title || '-'}</strong>
                  <small>{formatActionDate(item.createdAt || item.date)}</small>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const translations = {
  ru: {
    adminPanel: 'Административная панель',
    department: 'Кафедра информационных технологий',
    notifications: 'Уведомления',
    noNewNotifications: 'Новых нет',
    newNotifications: 'новых',
    allRead: 'Все прочитано',
    emptyNotifications: 'Уведомлений пока нет',
    notificationDefaultTitle: 'Уведомление',
    notificationDefaultText: 'Новое действие на портале',
    profile: 'Профиль',
    logout: 'Выйти',
    emailNotSet: 'email не указан',
    phoneNotSet: 'телефон не указан',
    adminDefault: 'Администратор',
    adminPanelShort: 'Админ панель',

    sidebarDashboard: 'Главная',
    sidebarUsers: 'Пользователи',
    sidebarTeachers: 'Преподаватели',
    sidebarStudents: 'Студенты',
    sidebarSchedule: 'Расписание',
    sidebarPlans: 'Учебные планы',
    sidebarPrograms: 'Рабочие программы',
    sidebarDocuments: 'Документы',
    sidebarReports: 'Отчеты',
    sidebarPractices: 'Практика',
    sidebarAchievements: 'Достижения',
    sidebarSettings: 'Настройки',

    sidebarLogoTitle: 'Кафедра информационных технологий',
    sidebarLogoSubtitle: 'Панель администратора',
    logoAlt: 'Кафедра информационных технологий',

    dashboardTitle: 'Главная панель',
    dashboardSubtitle: 'Обзор системы и ключевые показатели',
    totalUsers: 'Всего пользователей',
    students: 'Студентов',
    teachers: 'Преподавателей',
    disciplines: 'Дисциплин',
    plans: 'Учебные планы',
    programs: 'Рабочие программы',
    documents: 'Документы',
    reports: 'Отчеты',
    practices: 'Практика',
    achievements: 'Достижения',
    groups: 'Группы',
    portalActivity: 'Активность на портале',
    week: 'За неделю',
    month: 'За месяц',
    year: 'За год',
    recentActions: 'Последние действия',
    showAll: 'Смотреть все',
    quickActions: 'Быстрые действия',

    addTeacher: 'Добавить преподавателя',
    addStudent: 'Добавить студента',
    addProgram: 'Добавить программу',
    addDocument: 'Добавить документ',
    addReport: 'Добавить отчет',
    addPractice: 'Добавить практику',
    addAchievement: 'Добавить достижение',

    footer: 'Кафедра информационных технологий',
    version: 'Версия 1.0.0',
    loading: 'Загрузка...',
    registered: '✅ Зарегистрирован',
    pending: '⏳ Ожидает',
    active: 'Активная',
    inactive: 'Неактивная',
    activityEmpty: 'Активности пока нет. Когда вы добавите данные, график начнет работать.',
    actionsEmpty: 'Действий пока нет. Когда вы что-то добавите, измените или удалите, это появится здесь.',
    allActions: 'Все действия',
    noActions: 'Действий нет',

    profileTitle: 'Профиль администратора',
    fullNameLabel: 'Аты-жөнү',
    emailLabel: 'Email',
    phoneLabel: 'Телефон',
    positionLabel: 'Кызматы',
    departmentLabel: 'Кафедра',
    fioPlaceholder: 'ФИО',
    phonePlaceholder: 'Телефон',
    departmentPlaceholder: 'Кафедра',
    positionPlaceholder: 'Должность',
    bioPlaceholder: 'Краткая информация',
    save: 'Сохранить',
    cancel: 'Отмена',
    profileSaved: '✅ Профиль успешно обновлен!',
    profileError: '❌ Ошибка: ',

    newUserNotification: 'Новый пользователь',
    userAddedText: 'Добавлен',
    newProgramNotification: 'Новая программа',
    newDocumentNotification: 'Новый документ',
    newReportNotification: 'Новый отчет',
    newPracticeNotification: 'Новая практика',
    newAchievementNotification: 'Новое достижение',

    withoutName: 'Без названия',
    promptNewYear: 'Введите новый учебный год. Например: 2027-2028',

    deleteUserConfirm: 'Удалить пользователя?',
    deleteGroupConfirm: 'Удалить группу?',
    deleteProgramConfirm: 'Удалить программу?',
    deleteDocumentConfirm: 'Удалить документ?',
    deleteReportConfirm: 'Удалить отчет?',
    deletePracticeConfirm: 'Удалить практику?',
    deleteAchievementConfirm: 'Удалить достижение?',
    error: 'Ошибка: ',

    settingsTitle: 'Настройки портала',
    settingsSubtitle: 'Язык, тема',
    interfaceLanguage: 'Язык интерфейса',
    themeMode: 'Тема интерфейса',
    lightTheme: 'Светлая тема',
    darkTheme: 'Темная тема',
    academicYear: 'Учебный год',
    enabled: 'Включено',
    promoteStudents: 'Обновить учебный год',
    promoteConfirm: 'Обновить учебный год? 1 курс станет 2 курсом, 2 курс станет 3 курсом, 3 курс станет 4 курсом, а 4 курс перейдет в выпускники.',
    promoteSuccess: 'Учебный год успешно обновлен',
    noStudents: 'Студенты пока не добавлены',
    academicCalendar: 'Календарь',
    selectedDate: 'Выбранная дата',

    actionLanguageChanged: 'Язык изменен',
    actionDarkModeOn: 'Темная тема включена',
    actionLightModeOn: 'Светлая тема включена',
    actionProfileUpdated: 'Профиль администратора обновлен',
    actionUserAdded: 'Добавлен пользователь',
    actionUserUpdated: 'Изменен пользователь',
    actionUserDeleted: 'Удален пользователь',
    actionGroupAdded: 'Создана группа',
    actionGroupUpdated: 'Изменена группа',
    actionGroupDeleted: 'Удалена группа',
    actionProgramAdded: 'Добавлена учебная программа',
    actionProgramUpdated: 'Изменена учебная программа',
    actionProgramDeleted: 'Удалена учебная программа',
    actionDocumentAdded: 'Добавлен документ',
    actionDocumentUpdated: 'Изменен документ',
    actionDocumentDeleted: 'Удален документ',
    actionReportAdded: 'Добавлен отчет',
    actionReportUpdated: 'Изменен отчет',
    actionReportDeleted: 'Удален отчет и связанные ответы',
    actionPracticeAdded: 'Добавлена практика',
    actionPracticeUpdated: 'Изменена практика',
    actionPracticeDeleted: 'Удалена практика',
    actionAchievementAdded: 'Добавлено достижение',
    actionAchievementUpdated: 'Изменено достижение',
    actionAchievementDeleted: 'Удалено достижение',
    actionYearChanged: 'Учебный год изменен',
    actionYearAdded: 'Добавлен учебный год',

    notificationProgramForTeachers: 'Администратор отправил учебную программу',
    notificationDocumentForTeachers: 'Администратор отправил документ',
    notificationReportForTeachers: 'Администратор отправил отчет',
    notificationPracticeForTeachers: 'Администратор отправил практику',
    notificationAchievementForTeachers: 'Администратор отправил достижение',
    allTeachers: 'Все преподаватели',
    deletePhoto: 'Удалить фото',
    weekDays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    monthWeeks: ['1 нед', '2 нед', '3 нед', '4 нед'],
    yearMonths: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']
  },

  kg: {
    adminPanel: 'Администратор панели',
    department: 'Маалыматтык технологиялар кафедрасы',
    notifications: 'Билдирүүлөр',
    noNewNotifications: 'Жаңы билдирүү жок',
    newNotifications: 'жаңы',
    allRead: 'Баарын окулду кылуу',
    emptyNotifications: 'Азырынча билдирүү жок',
    notificationDefaultTitle: 'Билдирүү',
    notificationDefaultText: 'Порталда жаңы аракет болду',
    profile: 'Профиль',
    logout: 'Чыгуу',
    emailNotSet: 'email көрсөтүлгөн эмес',
    phoneNotSet: 'телефон көрсөтүлгөн эмес',
    adminDefault: 'Администратор',
    adminPanelShort: 'Админ панель',

    sidebarDashboard: 'Башкы бет',
    sidebarUsers: 'Колдонуучулар',
    sidebarTeachers: 'Окутуучулар',
    sidebarStudents: 'Студенттер',
    sidebarSchedule: 'Окуу жүгүртмөсү',
    sidebarPlans: 'Окуу пландары',
    sidebarPrograms: 'Жумушчу программалары',
    sidebarReports: 'Отчеттор',
    

    sidebarLogoTitle: 'Маалыматтык технологиялар кафедрасы',
    sidebarLogoSubtitle: 'Администратор панели',
    logoAlt: 'Кафедра порталы',

    dashboardTitle: 'Башкы панель',
    dashboardSubtitle: 'Системанын жалпы абалы жана негизги көрсөткүчтөр',
    totalUsers: 'Бардык колдонуучулар',
    students: 'Студенттер',
    teachers: 'Окутуучулар',
    disciplines: 'Окуу жүгүртмөсү',
    plans: 'Окуу пландары',
    programs: 'Жумушчу программалары',
    reports: 'Отчеттор',
    groups: 'Группалар',
    portalActivity: 'Порталдагы активдүүлүк',
    week: 'Апта боюнча',
    month: 'Ай боюнча',
    year: 'Жыл боюнча',
    recentActions: 'Акыркы аракеттер',
    showAll: 'Баарын көрүү',
    quickActions: 'Тез аракеттер',

    addTeacher: 'Окутуучу кошуу',
    addProgram: 'Программа кошуу',
    addReport: 'Отчет кошуу',
  

    footer: 'Кафедра порталы',
    loading: 'Жүктөлүүдө...',
    registered: '✅ Катталган',
    pending: '⏳ Күтүүдө',
    active: 'Активдүү',
    inactive: 'Активдүү эмес',
    activityEmpty: 'Азырынча активдүүлүк жок. Маалымат кошкондо график иштей баштайт.',
    actionsEmpty: 'Азырынча аракеттер жок. Бир нерсе кошкондо, өзгөрткөндө же өчүргөндө бул жерге жазылат.',
    allActions: 'Бардык аракеттер',
    noActions: 'Аракеттер жок',

    profileTitle: 'Администратордун профили',
    fullNameLabel: 'Аты-жөнү',
    emailLabel: 'Email',
    phoneLabel: 'Телефон',
    positionLabel: 'Кызматы',
    departmentLabel: 'Кафедра',
    fioPlaceholder: 'Аты-жөнү',
    phonePlaceholder: 'Телефон',
    departmentPlaceholder: 'Кафедра',
    positionPlaceholder: 'Кызматы',
    bioPlaceholder: 'Кыскача маалымат',
    save: 'Сактоо',
    cancel: 'Жокко чыгаруу',
    profileSaved: '✅ Профиль ийгиликтүү жаңыртылды!',
    profileError: '❌ Ката кетти: ',

    newUserNotification: 'Жаңы колдонуучу',
    userAddedText: 'Кошулду',
    newProgramNotification: 'Жаңы программа',
    newReportNotification: 'Жаңы отчет',
    

    withoutName: 'Аты жок',
    promptNewYear: 'Жаңы окуу жылын жазыңыз. Мисалы: 2027-2028',

    deleteUserConfirm: 'Колдонуучуну өчүрөсүзбү?',
    deleteGroupConfirm: 'Группаны өчүрөсүзбү?',
    deleteProgramConfirm: 'Жумушчу программасын өчүрөсүзбү?',
    deleteDocumentConfirm: 'Документти өчүрөсүзбү?',
    deleteReportConfirm: 'Отчетту өчүрөсүзбү?',
    error: 'Ката: ',

    settingsSubtitle: 'Тил, тема',
    interfaceLanguage: 'Интерфейс тили',
    academicYear: 'Окуу жылы',
    enabled: 'Иштеп турат',
    academicCalendar: 'Календарь',
    selectedDate: 'Тандалган дата',

    actionLanguageChanged: 'Тил өзгөртүлдү',
    actionProfileUpdated: 'Администратордун профили жаңыртылды',
    actionUserAdded: 'Колдонуучу кошулду',
    actionUserUpdated: 'Колдонуучу өзгөртүлдү',
    actionUserDeleted: 'Колдонуучу өчүрүлдү',
    actionProgramAdded: 'Жумушчу программасы кошулду',
    actionProgramUpdated: 'Жумушчу программасы өзгөртүлдү',
    actionProgramDeleted: 'Жумушчу программасы өчүрүлдү',
    actionReportAdded: 'Отчет кошулду',
    actionReportUpdated: 'Отчет өзгөртүлдү',
    actionReportDeleted: 'Отчетөчүрүлдү',
    actionYearChanged: 'Окуу жылы өзгөртүлдү',
    actionYearAdded: 'Жаңы окуу жылы кошулду',

    notificationProgramForTeachers: 'Администратор жумушчу программасын жиберди',
    notificationReportForTeachers: 'Администратор отчет жиберди',
    notificationAchievementForTeachers: 'Администратор жетишкендик жиберди',
    allTeachers: 'Бардык окутуучулар',
    deletePhoto: 'Сүрөттү өчүрүү',
    weekDays: ['Дш', 'Шш', 'Шр', 'Бш', 'Жм', 'Иш', 'Жк'],
    monthWeeks: ['1 апта', '2 апта', '3 апта', '4 апта'],
    yearMonths: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']
  },

  en: {
    adminPanel: 'Admin Panel',
    department: 'Department of Information Technologies',
    notifications: 'Notifications',
    noNewNotifications: 'No new notifications',
    newNotifications: 'new',
    allRead: 'Mark all as read',
    emptyNotifications: 'No notifications yet',
    notificationDefaultTitle: 'Notification',
    notificationDefaultText: 'New activity on the portal',
    profile: 'Profile',
    logout: 'Logout',
    emailNotSet: 'email not specified',
    phoneNotSet: 'phone not specified',
    adminDefault: 'Administrator',
    adminPanelShort: 'Admin panel',

    sidebarDashboard: 'Dashboard',
    sidebarUsers: 'Users',
    sidebarTeachers: 'Teachers',
    sidebarStudents: 'Students',
    sidebarSchedule: 'Schedule',
    sidebarPlans: 'Workload',
    sidebarPrograms: 'Work programs',
    sidebarDocuments: 'Documents',
    sidebarReports: 'Reports',
    sidebarPractices: 'Practice',
    sidebarAchievements: 'Achievements',
    sidebarSettings: 'Settings',

    sidebarLogoTitle: 'Department of Information Technologies',
    sidebarLogoSubtitle: 'Administrator panel',
    logoAlt: 'Department portal',

    dashboardTitle: 'Dashboard',
    dashboardSubtitle: 'System overview and key indicators',
    totalUsers: 'Total users',
    students: 'Students',
    teachers: 'Teachers',
    disciplines: 'Disciplines',
    plans: 'Workload',
    programs: 'Work programs',
    documents: 'Documents',
    reports: 'Reports',
    practices: 'Practice',
    achievements: 'Achievements',
    groups: 'Groups',
    portalActivity: 'Portal activity',
    week: 'Week',
    month: 'Month',
    year: 'Year',
    recentActions: 'Recent actions',
    showAll: 'Show all',
    quickActions: 'Quick actions',

    addTeacher: 'Add teacher',
    addStudent: 'Add student',
    addProgram: 'Add program',
    addDocument: 'Add document',
    addReport: 'Add report',
    addPractice: 'Add practice',
    addAchievement: 'Add achievement',

    footer: 'Department portal',
    version: 'Version 1.0.0',
    loading: 'Loading...',
    registered: '✅ Registered',
    pending: '⏳ Pending',
    active: 'Active',
    inactive: 'Inactive',
    activityEmpty: 'No activity yet. When you add data, the chart will start working.',
    actionsEmpty: 'No actions yet. When you add, edit or delete something, it will appear here.',
    allActions: 'All actions',
    noActions: 'No actions',

    profileTitle: 'Administrator profile',
    fullNameLabel: 'Full name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    positionLabel: 'Position',
    departmentLabel: 'Department',
    fioPlaceholder: 'Full name',
    phonePlaceholder: 'Phone',
    departmentPlaceholder: 'Department',
    positionPlaceholder: 'Position',
    bioPlaceholder: 'Brief information',
    save: 'Save',
    cancel: 'Cancel',
    profileSaved: '✅ Profile updated successfully!',
    profileError: '❌ Error: ',

    newUserNotification: 'New user',
    userAddedText: 'Added',
    newProgramNotification: 'New program',
    newDocumentNotification: 'New document',
    newReportNotification: 'New report',
    newPracticeNotification: 'New practice',
    newAchievementNotification: 'New achievement',

    withoutName: 'Untitled',
    promptNewYear: 'Enter a new academic year. Example: 2027-2028',

    deleteUserConfirm: 'Delete user?',
    deleteGroupConfirm: 'Delete group?',
    deleteProgramConfirm: 'Delete program?',
    deleteDocumentConfirm: 'Delete document?',
    deleteReportConfirm: 'Delete report?',
    deletePracticeConfirm: 'Delete practice?',
    deleteAchievementConfirm: 'Delete achievement?',
    error: 'Error: ',

    settingsTitle: 'Portal settings',
    settingsSubtitle: 'Language, theme',
    interfaceLanguage: 'Interface language',
    themeMode: 'Interface theme',
    lightTheme: 'Light theme',
    darkTheme: 'Dark theme',
    academicYear: 'Academic year',
    enabled: 'Enabled',
    promoteStudents: 'Promote academic year',
    promoteConfirm: 'Promote students to the next academic year? 1st year becomes 2nd, 2nd becomes 3rd, 3rd becomes 4th, and 4th becomes graduates.',
    promoteSuccess: 'Academic year updated successfully',
    noStudents: 'No students added yet',
    academicCalendar: 'Calendar',
    selectedDate: 'Selected date',

    actionLanguageChanged: 'Language changed',
    actionDarkModeOn: 'Dark theme enabled',
    actionLightModeOn: 'Light theme enabled',
    actionProfileUpdated: 'Administrator profile updated',
    actionUserAdded: 'User added',
    actionUserUpdated: 'User updated',
    actionUserDeleted: 'User deleted',
    actionGroupAdded: 'Group created',
    actionGroupUpdated: 'Group updated',
    actionGroupDeleted: 'Group deleted',
    actionProgramAdded: 'Work program added',
    actionProgramUpdated: 'Work program updated',
    actionProgramDeleted: 'Work program deleted',
    actionDocumentAdded: 'Document added',
    actionDocumentUpdated: 'Document updated',
    actionDocumentDeleted: 'Document deleted',
    actionReportAdded: 'Report added',
    actionReportUpdated: 'Report updated',
    actionReportDeleted: 'Report and related replies deleted',
    actionPracticeAdded: 'Practice added',
    actionPracticeUpdated: 'Practice updated',
    actionPracticeDeleted: 'Practice deleted',
    actionAchievementAdded: 'Achievement added',
    actionAchievementUpdated: 'Achievement updated',
    actionAchievementDeleted: 'Achievement deleted',
    actionYearChanged: 'Academic year changed',
    actionYearAdded: 'Academic year added',

    notificationProgramForTeachers: 'Administrator sent a work program',
    notificationDocumentForTeachers: 'Administrator sent a document',
    notificationReportForTeachers: 'Administrator sent a report',
    notificationPracticeForTeachers: 'Administrator sent a practice file',
    notificationAchievementForTeachers: 'Administrator sent an achievement',
    allTeachers: 'All teachers',
    deletePhoto: 'Delete photo',
    weekDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    monthWeeks: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    yearMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const footerCalendarRef = useRef(null);

  const [showFooterCalendar, setShowFooterCalendar] = useState(false);
  const [footerSelectedDate, setFooterSelectedDate] = useState(new Date());
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [language, setLanguage] = useState(() => localStorage.getItem('siteLanguage') || 'ru');
  const [currentYear, setCurrentYear] = useState(() => localStorage.getItem('academicYear') || '2025-2026');
  const [availableYears, setAvailableYears] = useState(['2024-2025', '2025-2026', '2026-2027']);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAllActionsModal, setShowAllActionsModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);


  const [adminProfile, setAdminProfile] = useState({});

  const [users, setUsers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [plans, setPlans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reports, setReports] = useState([]);
  const [practices, setPractices] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [activityData, setActivityData] = useState({ week: [], month: [], year: [] });
  const [recentActions, setRecentActions] = useState([]);

  const fetchTabData = async (menu) => {
    setLoading(true);
    try {
      if (menu === 'plans') {
        const discSnap = await getDocs(collection(db, 'disciplines', currentYear, 'items'));
        setDisciplines(firestoreSnapshotToArray(discSnap));
        
        const plansSnap = await getDocs(collection(db, 'plans', currentYear, 'items'));
        setPlans(firestoreSnapshotToArray(plansSnap));
      } else if (menu === 'programs') {
        const progSnap = await getDocs(collection(db, 'programs'));
        setPrograms(firestoreSnapshotToArray(progSnap));
      } else if (menu === 'documents') {
        const docsSnap = await getDocs(collection(db, 'documents'));
        setDocuments(firestoreSnapshotToArray(docsSnap));
      } else if (menu === 'reports') {
        const reportsSnap = await getDocs(collection(db, 'reports'));
        setReports(firestoreSnapshotToArray(reportsSnap));
      } else if (menu === 'practices') {
        const pracSnap = await getDocs(collection(db, 'practices'));
        setPractices(firestoreSnapshotToArray(pracSnap));
      } else if (menu === 'achievements') {
        const achSnap = await getDocs(collection(db, 'achievements'));
        setAchievements(firestoreSnapshotToArray(achSnap));
      }
    } catch (err) {
      console.error(`Error loading data for tab ${menu}:`, err);
    } finally {
      setLoading(false);
    }
  };

  const t = translations[language] || translations.ru;

  const getMonthTitle = (date) => {
    const months = monthNames[language] || monthNames.ru;
    return `${months[date.getMonth()] || ''} ${date.getFullYear()}`;
  };
  
const isAdminIncomingNotification = (item = {}) => {
  const receiverRole = normalizeText(item.receiverRole || '');
  const receiverId = String(item.receiverId || '');
  const senderRole = normalizeText(item.senderRole || '');
  const senderId = String(item.senderId || '');

  const sentByAdmin = senderRole === 'admin' || senderId === ADMIN_ID;
  const forAdmin =
    receiverRole === 'admin' ||
    receiverId === ADMIN_ID ||
    item.receiverType === 'admin';

  return forAdmin && !sentByAdmin;
};

  const adminNotifications = useMemo(() => {
  return notifications
    .filter(isAdminIncomingNotification)
    .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
}, [notifications]);

  const [statsCounts, setStatsCounts] = useState({
    totalUsers: 0,
    totalStudents: 0,
    totalTeachers: 0,
    totalGroups: 0,
    totalDisciplines: 0,
    totalPlans: 0,
    totalPrograms: 0,
    totalDocuments: 0,
    totalReports: 0,
    totalPractices: 0,
    totalAchievements: 0
  });

  const stats = useMemo(() => ({
    totalUsers: users.length || statsCounts.totalUsers,
    totalStudents: students.length || statsCounts.totalStudents,
    totalTeachers: teachers.length || statsCounts.totalTeachers,
    totalGroups: groups.length || statsCounts.totalGroups,
    totalDisciplines: disciplines.length || statsCounts.totalDisciplines,
    totalPlans: plans.length || statsCounts.totalPlans,
    totalPrograms: programs.length || statsCounts.totalPrograms,
    totalDocuments: documents.length || statsCounts.totalDocuments,
    totalReports: reports.filter((item) => !item.parentReportId).length || statsCounts.totalReports,
    totalPractices: practices.length || statsCounts.totalPractices,
    totalAchievements: achievements.length || statsCounts.totalAchievements
  }), [
    users.length,
    students.length,
    teachers.length,
    groups.length,
    disciplines.length,
    plans.length,
    programs.length,
    documents.length,
    reports,
    practices.length,
    achievements.length,
    statsCounts
  ]);

  const registeredUsers = useMemo(() => {
    return users.filter((item) => item.isRegistered === true || item.hasLoggedIn === true);
  }, [users]);

  const getCollectionData = async (collectionName) => {
    const snap = await getDocs(collection(db, collectionName));
    return firestoreSnapshotToArray(snap);
  };

  const getYearCollectionData = async (collectionName, year) => {
    const snap = await getDocs(collection(db, collectionName, year, 'items'));
    return firestoreSnapshotToArray(snap);
  };

  const getDocumentData = async (collectionName, documentId) => {
    const snap = await getDoc(doc(db, collectionName, documentId));

    if (!snap.exists()) return null;

    return {
      id: snap.id,
      ...(snap.data() || {})
    };
  };

  const addCollectionData = async (collectionName, data = {}) => {
    const docRef = doc(collection(db, collectionName));
    const cleanData = removeUndefinedFields({
      ...data,
      id: docRef.id,
      createdAt: data.createdAt || Date.now(),
      updatedAt: Date.now()
    });

    await setDoc(docRef, cleanData);

    return cleanData;
  };

  const saveDocumentData = async (collectionName, documentId, data = {}) => {
    const cleanData = removeUndefinedFields({
      ...data,
      id: documentId,
      updatedAt: Date.now()
    });

    await setDoc(doc(db, collectionName, documentId), cleanData, { merge: true });

    return {
      id: documentId,
      ...cleanData
    };
  };

  const updateDocumentData = async (collectionName, documentId, data = {}) => {
    const cleanData = removeUndefinedFields({
      ...data,
      updatedAt: Date.now()
    });

    console.log(`[Firestore Write] Updating document in collection "${collectionName}" with ID "${documentId}"`, {
      currentUser: auth.currentUser ? {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
        emailVerified: auth.currentUser.emailVerified
      } : null,
      data: cleanData
    });

    await updateDoc(doc(db, collectionName, documentId), cleanData);
  };

  const deleteDocumentData = async (collectionName, documentId) => {
    await deleteDoc(doc(db, collectionName, documentId));
  };

  const saveCollectionRows = async (collectionName, rows = []) => {
    const batch = writeBatch(db);

    rows.forEach((row) => {
      const id =
        row.id && !String(row.id).startsWith('new_') && !String(row.id).includes('_temp_')
          ? row.id
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      const rowRef = doc(db, collectionName, id);

      batch.set(
        rowRef,
        removeUndefinedFields({
          ...row,
          id,
          createdAt: row.createdAt || Date.now(),
          updatedAt: Date.now()
        }),
        { merge: true }
      );
    });

    await batch.commit();
  };

  const saveYearCollectionRows = async (collectionName, year, rows = []) => {
    const batch = writeBatch(db);

    rows.forEach((row) => {
      const id =
        row.id && !String(row.id).startsWith('new_') && !String(row.id).includes('_temp_')
          ? row.id
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      const rowRef = doc(db, collectionName, year, 'items', id);

      batch.set(
        rowRef,
        removeUndefinedFields({
          ...row,
          id,
          year,
          createdAt: row.createdAt || Date.now(),
          updatedAt: Date.now()
        }),
        { merge: true }
      );
    });

    await batch.commit();
  };

  const addActivityLog = async ({
    icon = '✅',
    text = '',
    type = 'system',
    senderRole = 'admin',
    senderId = ADMIN_ID,
    senderName = adminProfile.name || adminProfile.fullName || t.adminDefault,
    senderEmail = adminProfile.email || '',
    receiverRole = '',
    receiverType = '',
    receiverId = '',
    receiverName = '',
    receiverEmail = '',
    extra = {}
  }) => {
    try {
      await addCollectionData('activityLogs', {
        icon,
        text,
        type,
        senderRole,
        senderId,
        senderName,
        senderEmail,
        receiverRole,
        receiverType,
        receiverId,
        receiverName,
        receiverEmail,
        createdAt: Date.now(),
        ...extra
      });
    } catch (error) {
      console.log('Activity log error:', error);
    }
  };

  const addNotification = async ({
    title = t.notificationDefaultTitle,
    text = t.notificationDefaultText,
    icon = '🔔',
    type = 'info',
    senderRole = 'admin',
    senderId = ADMIN_ID,
    senderName = adminProfile.name || adminProfile.fullName || t.adminDefault,
    senderEmail = adminProfile.email || '',
    receiverRole = 'admin',
    receiverType = 'single',
    receiverId = ADMIN_ID,
    receiverName = t.adminDefault,
    receiverEmail = '',
    receiverTeacherId = '',
    receiverTeacherName = '',
    receiverTeacherEmail = '',
    toAllTeachers = false,
    extra = {}
  }) => {
    try {
      await addCollectionData('notifications', {
        title,
        text,
        icon,
        type,
        senderRole,
        senderId,
        senderName,
        senderEmail,
        receiverRole,
        receiverType,
        receiverId,
        receiverName,
        receiverEmail,
        receiverTeacherId,
        receiverTeacherName,
        receiverTeacherEmail,
        toAllTeachers,
        isRead: false,
        readBy: {},
        createdAt: Date.now(),
        ...extra
      });
    } catch (error) {
      console.log('Notification error:', error);
    }
  };

  const addNotificationToAdmin = async ({
    title = t.notificationDefaultTitle,
    text = t.notificationDefaultText,
    icon = '🔔',
    type = 'info',
    senderRole = 'teacher',
    senderId = '',
    senderName = '',
    senderEmail = '',
    extra = {}
    
  }) => {
    if (normalizeText(senderRole) === 'admin' || String(senderId) === ADMIN_ID) {
  return;
}
    await addNotification({
      title,
      text,
      icon,
      type,
      senderRole,
      senderId,
      senderName,
      senderEmail,
      receiverRole: 'admin',
      receiverType: 'single',
      receiverId: ADMIN_ID,
      receiverName: adminProfile.name || adminProfile.fullName || t.adminDefault,
      receiverEmail: adminProfile.email || '',
      extra
    });
  };

  const addNotificationToTeacher = async ({
    teacherId = '',
    teacherName = '',
    teacherEmail = '',
    title = t.notificationDefaultTitle,
    text = t.notificationDefaultText,
    icon = '🔔',
    type = 'info',
    extra = {}
  }) => {
    if (!teacherId && !teacherEmail) return;

    await addNotification({
      title,
      text,
      icon,
      type,
      receiverRole: 'teacher',
      receiverType: 'single',
      receiverId: teacherId,
      receiverName: teacherName,
      receiverEmail: teacherEmail,
      receiverTeacherId: teacherId,
      receiverTeacherName: teacherName,
      receiverTeacherEmail: teacherEmail,
      extra
    });
  };

  const addNotificationToAllTeachers = async ({
    title = t.notificationDefaultTitle,
    text = t.notificationDefaultText,
    icon = '🔔',
    type = 'info',
    senderRole = 'admin',
    senderId = ADMIN_ID,
    senderName = adminProfile.name || adminProfile.fullName || t.adminDefault,
    senderEmail = adminProfile.email || '',
    extra = {}
  }) => {
    await addNotification({
      title,
      text,
      icon,
      type,
      senderRole,
      senderId,
      senderName,
      senderEmail,
      receiverRole: 'teacher',
      receiverType: 'all',
      receiverId: 'all_teachers',
      receiverName: t.allTeachers,
      toAllTeachers: true,
      extra
    });
  };

  const loadAdminProfile = async () => {
    try {
      const data = await getDocumentData('users', ADMIN_ID);

      if (data) {
        if (auth.currentUser && (!data.authUid || !data.email)) {
          await updateDocumentData('users', ADMIN_ID, {
            authUid: auth.currentUser.uid,
            email: auth.currentUser.email,
            emailLower: String(auth.currentUser.email || '').toLowerCase()
          });
        }
        setAdminProfile((prev) => ({
          ...prev,
          ...data,
          id: ADMIN_ID,
          role: 'admin'
        }));
      } else {
        await saveDocumentData('users', ADMIN_ID, {
          ...adminProfile,
          id: ADMIN_ID,
          role: 'admin',
          authUid: auth.currentUser ? auth.currentUser.uid : '',
          email: auth.currentUser ? auth.currentUser.email : '',
          emailLower: auth.currentUser ? String(auth.currentUser.email || '').toLowerCase() : '',
          access: Object.fromEntries(
            Object.keys(defaultAccess).map((key) => [key, 'edit'])
          ),
          loginStatus: 'entered',
          hasLoggedIn: true,
          isRegistered: true
        });
      }
    } catch (error) {
      console.error('Error loading admin profile:', error);
    }
  };

  const buildActivityData = (actions = []) => {
  const today = new Date();

  const weekLabels = (t.weekDays && t.weekDays.length === 7)
    ? t.weekDays
    : ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const weekCounts = [0, 0, 0, 0, 0, 0, 0];

  const startOfWeek = new Date(today);
  const todayDay = today.getDay();
  const diffToMonday = todayDay === 0 ? -6 : 1 - todayDay;

  startOfWeek.setDate(today.getDate() + diffToMonday);
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  actions.forEach((item) => {
    const date = new Date(item.createdAt || item.date || 0);
    if (Number.isNaN(date.getTime())) return;
    if (date < startOfWeek || date > endOfWeek) return;

    const jsDay = date.getDay();
    const index = jsDay === 0 ? 6 : jsDay - 1;
    weekCounts[index] += 1;
  });

  const week = weekLabels.map((label, index) => ({
    label,
    value: weekCounts[index]
  }));

  const monthLabels = (t.monthWeeks && t.monthWeeks.length === 4)
    ? t.monthWeeks
    : ['1 нед', '2 нед', '3 нед', '4 нед'];
  const monthCounts = [0, 0, 0, 0];

  actions.forEach((item) => {
    const date = new Date(item.createdAt || item.date || 0);
    if (Number.isNaN(date.getTime())) return;

    const isCurrentMonth =
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth();

    if (!isCurrentMonth) return;

    const day = date.getDate();

    if (day >= 1 && day <= 7) monthCounts[0] += 1;
    else if (day >= 8 && day <= 14) monthCounts[1] += 1;
    else if (day >= 15 && day <= 21) monthCounts[2] += 1;
    else monthCounts[3] += 1;
  });

  const month = monthLabels.map((label, index) => ({
    label,
    value: monthCounts[index]
  }));

  const yearLabels = (t.yearMonths && t.yearMonths.length === 12)
    ? t.yearMonths
    : ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

  const yearCounts = Array.from({ length: 12 }, () => 0);

  actions.forEach((item) => {
    const date = new Date(item.createdAt || item.date || 0);
    if (Number.isNaN(date.getTime())) return;

    if (date.getFullYear() === today.getFullYear()) {
      yearCounts[date.getMonth()] += 1;
    }
  });

  const year = yearLabels.map((label, index) => ({
    label,
    value: yearCounts[index]
  }));

  setActivityData({ week, month, year });
};

  const loadStatsCounts = async () => {
    try {
      const [
        usersCount,
        groupsCount,
        programsCount,
        documentsCount,
        reportsCount,
        practicesCount,
        achievementsCount,
        disciplinesCount,
        plansCount
      ] = await Promise.all([
        getCountFromServer(collection(db, 'users')),
        getCountFromServer(collection(db, 'groups')),
        getCountFromServer(collection(db, 'programs')),
        getCountFromServer(collection(db, 'documents')),
        getCountFromServer(query(collection(db, 'reports'), where('parentReportId', '==', null))),
        getCountFromServer(collection(db, 'practices')),
        getCountFromServer(collection(db, 'achievements')),
        getCountFromServer(collection(db, 'disciplines', currentYear, 'items')),
        getCountFromServer(collection(db, 'plans', currentYear, 'items'))
      ]);

      const [studentsCount, teachersCount] = await Promise.all([
        getCountFromServer(query(collection(db, 'users'), where('role', '==', 'student'))),
        getCountFromServer(query(collection(db, 'users'), where('role', '==', 'teacher')))
      ]);

      setStatsCounts({
        totalUsers: usersCount.data().count,
        totalStudents: studentsCount.data().count,
        totalTeachers: teachersCount.data().count,
        totalGroups: groupsCount.data().count,
        totalDisciplines: disciplinesCount.data().count,
        totalPlans: plansCount.data().count,
        totalPrograms: programsCount.data().count,
        totalDocuments: documentsCount.data().count,
        totalReports: reportsCount.data().count,
        totalPractices: practicesCount.data().count,
        totalAchievements: achievementsCount.data().count
      });
    } catch (e) {
      console.error('Stats loading error:', e);
    }
  };

  const loadAllData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      await loadStatsCounts();
    } catch (error) {
      console.error('Load admin data error:', error);
    } finally {
      setLoading(false);
    }
  };
  const saveRows = async ({
    rows = [],
    sourceList = [],
    addFn,
    updateFn,
    options = {}
  }) => {
    const sourceMap = new Map(sourceList.map((item) => [item.id, item]));

    const promises = rows.map(async (row) => {
      const isNew =
        !row.id ||
        String(row.id).startsWith('new_') ||
        String(row.id).includes('_temp_');

      if (isNew) {
        return addFn(row, { ...options, skipReload: true });
      }

      const oldRow = sourceMap.get(row.id);

      const hasChanges =
        (options.actionType === 'access' && options.targetUserId === row.id) ||
        !oldRow ||
        Object.keys(row).some(
          (key) =>
            key !== 'updatedAt' &&
            normalizeComparableValue(row[key]) !==
            normalizeComparableValue(oldRow[key])
        );

      if (hasChanges) {
        return updateFn(row.id, row, { ...options, skipReload: true });
      }
    });

    await Promise.all(promises);

    if (['plans', 'programs', 'documents', 'reports', 'practices', 'achievements'].includes(activeMenu)) {
      await fetchTabData(activeMenu);
    }

    if (!options?.skipReload) await loadAllData(true);
  };

  const addUser = async (data = {}, options = {}) => {
    const role = data.role || normalizePositionToRole(data.position || '', 'teacher');
    const fullName = data.fullName || data.name || '';

    const newUser = await addCollectionData('users', {
      ...data,
      fullName,
      name: fullName,
      role,
      position: data.position || (role === 'admin' ? 'Администратор' : role === 'student' ? 'Студент' : 'Преподаватель'),
      access: {
        ...defaultAccess,
        ...(data.access || {})
      },
      loginStatus: 'waiting',
      isRegistered: false,
      hasLoggedIn: false
    });

    await addNotificationToAdmin({
      title: t.newUserNotification,
      text: `${t.userAddedText}: ${fullName || data.email || t.withoutName}`,
      icon: '👤',
      type: 'user',
      senderRole: 'admin',
      senderId: ADMIN_ID,
      senderName: adminProfile.name || adminProfile.fullName
    });

    await addActivityLog({
      icon: '👤',
      text: `${t.actionUserAdded}: ${fullName || data.email || t.withoutName}`,
      type: 'user'
    });

    if (!options?.skipReload) await loadAllData(true);
    return newUser;
  };

  const updateUser = async (id, data = {}, options = {}) => {
    const role = data.role || normalizePositionToRole(data.position || '', 'teacher');
    const fullName = data.fullName || data.name || '';

    await updateDocumentData('users', id, {
      ...data,
      fullName,
      name: fullName,
      role,
      access: {
        ...defaultAccess,
        ...(data.access || {}),
        reportsCurator: data.access?.reportsCurator || data.access?.reports || 'none',
        reportsDepartment: data.access?.reportsDepartment || data.access?.reports || 'none'
      }
    });

    const shouldLog = options.shouldLog !== false;

    if (shouldLog) {
      const logText = options.customActionText ||
        data.lastActionText ||
        data.lastAccessLogText ||
        `${t.actionUserUpdated}: ${fullName || data.email || t.withoutName}`;

      await addActivityLog({
        icon: options.actionType === 'access' ? '🔐' : '✏️',
        text: logText,
        type: options.actionType || 'user',
        receiverId: options.targetUserId || id,
        receiverName: options.targetUserName || fullName || data.email || '',
        receiverEmail: data.email || '',
        extra: {
          actionType: options.actionType || 'user',
          targetUserId: options.targetUserId || id,
          targetUserName: options.targetUserName || fullName || data.email || ''
        }
      });
    }

    if (!options?.skipReload) await loadAllData(true);
  };

  const addGroup = async (data = {}, options = {}) => {
    const result = await addCollectionData('groups', data);
    await addActivityLog({ icon: '👥', text: `${t.actionGroupAdded}: ${data.name || t.withoutName}`, type: 'group' });
    if (!options?.skipReload) await loadAllData(true);
    return result;
  };

  const updateGroup = async (id, data = {}, options = {}) => {
    await updateDocumentData('groups', id, data);
    await addActivityLog({ icon: '✏️', text: `${t.actionGroupUpdated}: ${data.name || t.withoutName}`, type: 'group' });
  };

  // Функция для получения полей отправителя (админ или учитель)
  const getSenderFields = (senderRole = 'admin', senderData = null) => {
    if (senderRole === 'teacher' && senderData) {
      return {
        senderRole: 'teacher',
        senderId: senderData.id || senderData.teacherId || '',
        senderName: senderData.name || senderData.fullName || senderData.teacherName || '',
        senderEmail: senderData.email || '',
        createdBy: senderData.id || senderData.teacherId || '',
        createdByName: senderData.name || senderData.fullName || senderData.teacherName || '',
        createdByEmail: senderData.email || '',
        createdByRole: 'teacher',
        uploadedBy: senderData.id || senderData.teacherId || '',
        uploadedByName: senderData.name || senderData.fullName || senderData.teacherName || '',
        uploadedByEmail: senderData.email || '',
        uploadedByRole: 'teacher'
      };
    }
    
    return {
      senderRole: 'admin',
      senderId: ADMIN_ID,
      senderName: adminProfile.fullName || adminProfile.name || t.adminDefault,
      senderEmail: adminProfile.email || '',
      createdBy: ADMIN_ID,
      createdByName: adminProfile.fullName || adminProfile.name || t.adminDefault,
      createdByEmail: adminProfile.email || '',
      createdByRole: 'admin',
      uploadedBy: ADMIN_ID,
      uploadedByName: adminProfile.fullName || adminProfile.name || t.adminDefault,
      uploadedByEmail: adminProfile.email || '',
      uploadedByRole: 'admin'
    };
  };

  // Функция для получения полей получателя (все учителя)
  const getReceiverFieldsForAllTeachers = () => ({
    receiverRole: 'teacher',
    receiverType: 'all',
    receiverId: 'all_teachers',
    receiverName: t.allTeachers || 'Все преподаватели',
    targetRole: 'teacher',
    toAllTeachers: true,
    readByTeacher: false,
    readByAdmin: true,
    status: 'sent'
  });

  const addProgram = async (data = {}, options = {}) => {
    const senderRole = options.senderRole || 'admin';
    const senderData = options.senderData || null;
    
    const senderFields = getSenderFields(senderRole, senderData);
    const receiverFields = getReceiverFieldsForAllTeachers();
    
    const result = await addCollectionData('programs', {
      ...data,
      ...senderFields,
      ...receiverFields,
      sectionType: 'program',
      sourceSection: 'program',
      itemType: 'program'
    });

    await Promise.all([
      addNotificationToAllTeachers({
        title: t.newProgramNotification,
        text: `${t.notificationProgramForTeachers}: ${data.name || data.title || t.withoutName}`,
        icon: '📘',
        type: 'program',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail
      }),
      addActivityLog({
        icon: '📘',
        text: `${t.actionProgramAdded}: ${data.name || data.title || t.withoutName}`,
        type: 'program',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail,
        receiverRole: 'teacher',
        receiverType: 'all',
        receiverId: 'all_teachers',
        extra: { toAllTeachers: true }
      })
    ]);

    if (!options?.skipReload) await loadAllData(true);
    return result;
  };

  const updateProgram = async (id, data = {}, options = {}) => {
    await updateDocumentData('programs', id, {
      ...data,
      updatedAt: Date.now()
    });

    await addActivityLog({
      icon: '✏️',
      text: `${t.actionProgramUpdated}: ${data.name || data.title || t.withoutName}`,
      type: 'program',
      ...getSenderFields(options.senderRole || 'admin', options.senderData)
    });

    if (!options?.skipReload) await loadAllData(true);
  };

  const addDocument = async (data = {}, options = {}) => {
    const senderRole = options.senderRole || 'admin';
    const senderData = options.senderData || null;
    
    const senderFields = getSenderFields(senderRole, senderData);
    const receiverFields = getReceiverFieldsForAllTeachers();
    
    const result = await addCollectionData('documents', {
      ...data,
      ...senderFields,
      ...receiverFields,
      sectionType: 'document',
      sourceSection: 'document',
      itemType: 'document'
    });

    await Promise.all([
      addNotificationToAllTeachers({
        title: t.newDocumentNotification,
        text: `${t.notificationDocumentForTeachers}: ${data.title || data.name || t.withoutName}`,
        icon: '📄',
        type: 'document',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail
      }),
      addActivityLog({
        icon: '📄',
        text: `${t.actionDocumentAdded}: ${data.title || data.name || t.withoutName}`,
        type: 'document',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail,
        receiverRole: 'teacher',
        receiverType: 'all',
        receiverId: 'all_teachers',
        extra: { toAllTeachers: true }
      })
    ]);

    if (!options?.skipReload) await loadAllData(true);
    return result;
  };

  const updateDocument = async (id, data = {}, options = {}) => {
    await updateDocumentData('documents', id, {
      ...data,
      updatedAt: Date.now()
    });

    await addActivityLog({
      icon: '✏️',
      text: `${t.actionDocumentUpdated}: ${data.title || data.name || t.withoutName}`,
      type: 'document',
      ...getSenderFields(options.senderRole || 'admin', options.senderData)
    });

    if (!options?.skipReload) await loadAllData(true);
  };

  const addReport = async (data = {}, options = {}) => {
    const senderRole = options.senderRole || 'admin';
    const senderData = options.senderData || null;
    
    const senderFields = getSenderFields(senderRole, senderData);
    const receiverFields = getReceiverFieldsForAllTeachers();
    
    const result = await addCollectionData('reports', {
      ...data,
      ...senderFields,
      ...receiverFields,
      sectionType: 'report',
      sourceSection: 'report',
      itemType: 'report'
    });

    await Promise.all([
      addNotificationToAllTeachers({
        title: t.newReportNotification,
        text: `${t.notificationReportForTeachers}: ${data.title || data.name || t.withoutName}`,
        icon: '📊',
        type: 'report',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail
      }),
      addActivityLog({
        icon: '📊',
        text: `${t.actionReportAdded}: ${data.title || data.name || t.withoutName}`,
        type: 'report',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail,
        receiverRole: 'teacher',
        receiverType: 'all',
        receiverId: 'all_teachers',
        extra: { toAllTeachers: true }
      })
    ]);

    if (!options?.skipReload) await loadAllData(true);
    return result;
  };

  const updateReport = async (id, data = {}, options = {}) => {
    await updateDocumentData('reports', id, {
      ...data,
      updatedAt: Date.now()
    });

    await addActivityLog({
      icon: '✏️',
      text: `${t.actionReportUpdated}: ${data.title || data.name || t.withoutName}`,
      type: 'report',
      ...getSenderFields(options.senderRole || 'admin', options.senderData)
    });

    if (!options?.skipReload) await loadAllData(true);
  };

  const addPractice = async (data = {}, options = {}) => {
    const senderRole = options.senderRole || 'admin';
    const senderData = options.senderData || null;
    
    const senderFields = getSenderFields(senderRole, senderData);
    const receiverFields = getReceiverFieldsForAllTeachers();
    
    const result = await addCollectionData('practices', {
      ...data,
      ...senderFields,
      ...receiverFields,
      sectionType: 'practice',
      sourceSection: 'practice',
      itemType: 'practice',
      type: data.type || data.practiceType || ''
    });

    await Promise.all([
      addNotificationToAllTeachers({
        title: t.newPracticeNotification,
        text: `${t.notificationPracticeForTeachers}: ${data.type || data.practiceType || data.group || t.withoutName}`,
        icon: '🧳',
        type: 'practice',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail
      }),
      addActivityLog({
        icon: '🧳',
        text: `${t.actionPracticeAdded}: ${data.type || data.practiceType || data.group || t.withoutName}`,
        type: 'practice',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail,
        receiverRole: 'teacher',
        receiverType: 'all',
        receiverId: 'all_teachers',
        extra: { toAllTeachers: true }
      })
    ]);

    if (!options?.skipReload) await loadAllData(true);
    return result;
  };

  const updatePractice = async (id, data = {}, options = {}) => {
    await updateDocumentData('practices', id, {
      ...data,
      type: data.type || data.practiceType || '',
      updatedAt: Date.now()
    });

    await addActivityLog({
      icon: '✏️',
      text: `${t.actionPracticeUpdated}: ${data.type || data.practiceType || data.group || t.withoutName}`,
      type: 'practice',
      ...getSenderFields(options.senderRole || 'admin', options.senderData)
    });

    if (!options?.skipReload) await loadAllData(true);
  };

  const addAchievement = async (data = {}, options = {}) => {
    const senderRole = options.senderRole || 'admin';
    const senderData = options.senderData || null;
    
    const senderFields = getSenderFields(senderRole, senderData);
    const receiverFields = getReceiverFieldsForAllTeachers();
    
    const cleanAchievementType = data.type && data.type !== 'achievement'
      ? data.type
      : data.achievementType || '';

    const result = await addCollectionData('achievements', {
      ...data,
      ...senderFields,
      ...receiverFields,
      sectionType: 'achievement',
      sourceSection: 'achievement',
      itemType: 'achievement',
      type: cleanAchievementType,
      achievementType: cleanAchievementType,
      teacherName: data.teacherName || senderFields.senderName || t.adminDefault
    });

    await Promise.all([
      addNotificationToAllTeachers({
        title: t.newAchievementNotification,
        text: `${t.notificationAchievementForTeachers}: ${data.title || data.name || t.withoutName}`,
        icon: '🏆',
        type: 'achievement',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail
      }),
      addActivityLog({
        icon: '🏆',
        text: `${t.actionAchievementAdded}: ${data.title || data.name || t.withoutName}`,
        type: 'achievement',
        senderRole: senderFields.senderRole,
        senderId: senderFields.senderId,
        senderName: senderFields.senderName,
        senderEmail: senderFields.senderEmail,
        receiverRole: 'teacher',
        receiverType: 'all',
        receiverId: 'all_teachers',
        extra: { toAllTeachers: true }
      })
    ]);

    if (!options?.skipReload) await loadAllData(true);
    return result;
  };

  const updateAchievement = async (id, data = {}, options = {}) => {
    const cleanAchievementType = data.type && data.type !== 'achievement'
      ? data.type
      : data.achievementType || '';

    await updateDocumentData('achievements', id, {
      ...data,
      type: cleanAchievementType,
      achievementType: cleanAchievementType,
      updatedAt: Date.now()
    });

    await addActivityLog({
      icon: '✏️',
      text: `${t.actionAchievementUpdated}: ${data.title || data.name || t.withoutName}`,
      type: 'achievement',
      ...getSenderFields(options.senderRole || 'admin', options.senderData)
    });

    if (!options?.skipReload) await loadAllData(true);
  };

  const saveUsers = async (rows = [], options = {}) =>
    saveRows({
      rows,
      sourceList: users,
      addFn: addUser,
      updateFn: updateUser,
      options
    });

  const saveTeachers = async (rows = [], options = {}) =>
    saveRows({
      rows,
      sourceList: teachers,
      addFn: addUser,
      updateFn: updateUser,
      options: options.actionType === 'access'
        ? { ...options, shouldLog: false }
        : options
    });

  const saveStudents = async (rows = []) =>
    saveRows({ rows, sourceList: students, addFn: addUser, updateFn: updateUser });

  const saveGroups = async (rows = []) =>
    saveRows({ rows, sourceList: groups, addFn: addGroup, updateFn: updateGroup });

  const savePrograms = async (rows = []) =>
    saveRows({ rows, sourceList: programs, addFn: addProgram, updateFn: updateProgram });

  const saveDocuments = async (rows = []) =>
    saveRows({ rows, sourceList: documents, addFn: addDocument, updateFn: updateDocument });

  const saveReports = async (rows = []) =>
    saveRows({ rows, sourceList: reports, addFn: addReport, updateFn: updateReport });

  const savePractices = async (rows = []) =>
    saveRows({ rows, sourceList: practices, addFn: addPractice, updateFn: updatePractice });

  const saveAchievements = async (rows = []) =>
    saveRows({ rows, sourceList: achievements, addFn: addAchievement, updateFn: updateAchievement });

  const updateAdminProfile = async (profileData = {}) => {
  try {
    const preparedProfile = removeUndefinedFields({
      ...adminProfile,
      ...profileData,
      id: ADMIN_ID,
      uid: ADMIN_ID,
      role: 'admin',
      fullName: profileData.fullName !== undefined ? profileData.fullName : (profileData.name !== undefined ? profileData.name : (adminProfile.fullName || 'Администратор')),
      name: profileData.fullName !== undefined ? profileData.fullName : (profileData.name !== undefined ? profileData.name : (adminProfile.name || 'Администратор')),
      email: profileData.email !== undefined ? profileData.email : (adminProfile.email || ''),
      emailLower: String(profileData.email !== undefined ? profileData.email : (adminProfile.email || '')).trim().toLowerCase(),
      phone: profileData.phone !== undefined ? profileData.phone : (adminProfile.phone || ''),
      authUid: profileData.authUid || adminProfile.authUid || (auth.currentUser ? auth.currentUser.uid : ''),
      position: profileData.position !== undefined ? profileData.position : (adminProfile.position || 'Администратор'),
      department: profileData.department !== undefined ? profileData.department : (adminProfile.department || 'Кафедра информационных технологий'),
      bio: profileData.bio !== undefined ? profileData.bio : (profileData.info !== undefined ? profileData.info : (adminProfile.bio || adminProfile.info || '')),
      info: profileData.bio !== undefined ? profileData.bio : (profileData.info !== undefined ? profileData.info : (adminProfile.bio || adminProfile.info || '')),
      photoUrl: profileData.photoUrl !== undefined ? profileData.photoUrl : (profileData.avatar !== undefined ? profileData.avatar : (adminProfile.photoUrl || adminProfile.avatar || '')),
      avatar: profileData.photoUrl !== undefined ? profileData.photoUrl : (profileData.avatar !== undefined ? profileData.avatar : (adminProfile.photoUrl || adminProfile.avatar || '')),
      loginStatus: 'entered',
      hasLoggedIn: true,
      isRegistered: true,
      updatedAt: Date.now()
    });

    await saveDocumentData('users', ADMIN_ID, preparedProfile);

    setAdminProfile((prev) => ({
      ...prev,
      ...preparedProfile
    }));

    sessionStorage.setItem('currentUser', JSON.stringify(preparedProfile));
    sessionStorage.setItem('userRole', 'admin');
    sessionStorage.setItem('adminId', ADMIN_ID);
    sessionStorage.setItem('adminName', preparedProfile.fullName || preparedProfile.name || '');

    await addActivityLog({
      icon: '👤',
      text: t.actionProfileUpdated || 'Профиль администратора обновлен',
      type: 'profile'
    });

    alert(t.profileSaved || '✅ Профиль успешно обновлен!');
    return true;
  } catch (error) {
    alert((t.profileError || '❌ Ошибка: ') + error.message);
    return false;
  }
};

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    localStorage.setItem('siteLanguage', lang);

    await addActivityLog({
      icon: '🌐',
      text: `${t.actionLanguageChanged}: ${lang.toUpperCase()}`,
      type: 'language'
    });
  };

  const promoteStudentsToNextCourse = async () => {
    if (!window.confirm(t.promoteConfirm)) return;

    try {
      const batch = writeBatch(db);

      students.forEach((student) => {
        const currentCourse = Number(student.course || 1);
        const nextCourse = currentCourse >= 4 ? 'graduate' : String(currentCourse + 1);

        batch.set(
          doc(db, 'users', student.id),
          {
            course: nextCourse,
            status: nextCourse === 'graduate' ? 'graduate' : student.status || 'active',
            updatedAt: Date.now()
          },
          { merge: true }
        );
      });

      await batch.commit();
      await addActivityLog({ icon: '🎓', text: t.promoteSuccess, type: 'students' });
      alert(t.promoteSuccess);
      await loadAllData(true);
    } catch (error) {
      alert(t.error + error.message);
    }
  };

  const addAcademicYear = async () => {
    const year = window.prompt(t.promptNewYear);
    if (!year) return;

    const cleanYear = year.trim();
    if (!cleanYear) return;

    setAvailableYears((prev) => (prev.includes(cleanYear) ? prev : [...prev, cleanYear]));
    setCurrentYear(cleanYear);
    localStorage.setItem('academicYear', cleanYear);

    await addActivityLog({ icon: '📅', text: `${t.actionYearAdded}: ${cleanYear}`, type: 'year' });
  };

  const changeAcademicYear = async (year) => {
    setCurrentYear(year);
    localStorage.setItem('academicYear', year);

    await addActivityLog({ icon: '📅', text: `${t.actionYearChanged}: ${year}`, type: 'year' });
  };

  const markNotificationRead = async (notificationId) => {
    if (!notificationId) return;

    try {
      await setDoc(
        doc(db, 'notifications', notificationId),
        {
          isRead: true,
          readAt: Date.now(),
          readBy: { [ADMIN_ID]: true },
          updatedAt: Date.now()
        },
        { merge: true }
      );

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? {
                ...item,
                isRead: true,
                readAt: Date.now(),
                readBy: { ...(item.readBy || {}), [ADMIN_ID]: true }
              }
            : item
        )
      );
      setUnreadCount((prev) => Math.max(0, Number(prev || 0) - 1));
    } catch (error) {
      console.log('Notification read error:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const batch = writeBatch(db);

      adminNotifications.forEach((item) => {
        if (item.id) {
          batch.set(
            doc(db, 'notifications', item.id),
            {
              isRead: true,
              readAt: Date.now(),
              readBy: { ...(item.readBy || {}), [ADMIN_ID]: true },
              updatedAt: Date.now()
            },
            { merge: true }
          );
        }
      });

      await batch.commit();
      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          isRead: true,
          readAt: Date.now(),
          readBy: { ...(item.readBy || {}), [ADMIN_ID]: true }
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.log('Notification read error:', error);
    }
  };

  const openSection = (menuId) => {
    setActiveMenu(menuId);
  };

  const logout = () => {
    sessionStorage.removeItem('isAdminLoggedIn');
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('currentUser');
    sessionStorage.removeItem('adminId');
    sessionStorage.removeItem('teacherId');
    sessionStorage.removeItem('studentId');
    navigate('/login');
  };

  useEffect(() => {
    loadAdminProfile();
  }, []);

  useEffect(() => {
    const unsubscribes = [];
    setLoading(true);

    // 1. Scoped notifications listener (global - always runs to update unread badge in topbar, limit to latest 30)
    const qNotifications = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    const unsubNotifications = onSnapshot(qNotifications, (snapshot) => {
      const list = firestoreSnapshotToArray(snapshot);
      setNotifications(list);
      setUnreadCount(
        list.filter((item) => {
          const readBy = item.readBy || {};
          return (
            isAdminIncomingNotification(item) &&
            item.isRead === false &&
            readBy[ADMIN_ID] !== true
          );
        }).length
      );
    }, (err) => console.error('Notifications load error:', err));
    unsubscribes.push(unsubNotifications);

    // One-time fetch for static tabs
    if (['plans', 'programs', 'documents', 'reports', 'practices', 'achievements'].includes(activeMenu)) {
      fetchTabData(activeMenu);
    }

    // 2. Tab-specific subscriptions (lazy loading)
    if (activeMenu === 'dashboard') {
      // Subscribe to activity logs (limit 30 and order by createdAt desc)
      const logsQuery = query(
        collection(db, 'activityLogs'),
        orderBy('createdAt', 'desc'),
        limit(30)
      );
      const unsubLogs = onSnapshot(logsQuery, (snapshot) => {
        const list = firestoreSnapshotToArray(snapshot);
        const adminLogs = list.filter((item) => {
          const role = String(item.senderRole || '').toLowerCase();
          const sender = String(item.senderId || '');
          return role === 'admin' || sender === ADMIN_ID;
        });
        setRecentActions(adminLogs.slice(0, 30));
        buildActivityData(adminLogs);
      }, (err) => console.error('Logs load error:', err));
      unsubscribes.push(unsubLogs);

      // Load counts
      loadStatsCounts();
      setLoading(false);
    } else if (activeMenu === 'users' || activeMenu === 'teachers') {
      // Query only non-students (teachers, admins)
      const qNonStudents = query(
        collection(db, 'users'),
        where('role', 'in', ['teacher', 'admin'])
      );
      const unsubUsers = onSnapshot(qNonStudents, (snapshot) => {
        const list = firestoreSnapshotToArray(snapshot);
        const normalized = list.map((item) => normalizeUser(item.id, item));
        setUsers(normalized);
        setTeachers(normalized.filter((item) => item.role === 'teacher'));
        setLoading(false);
      }, (err) => { console.error('Users load error:', err); setLoading(false); });
      unsubscribes.push(unsubUsers);

      const unsubGroups = onSnapshot(collection(db, 'groups'), (snapshot) => {
        setGroups(firestoreSnapshotToArray(snapshot));
      }, (err) => console.error('Groups load error:', err));
      unsubscribes.push(unsubGroups);
    } else if (activeMenu === 'students') {
      // Query only students
      const qStudents = query(
        collection(db, 'users'),
        where('role', '==', 'student')
      );
      const unsubUsers = onSnapshot(qStudents, (snapshot) => {
        const list = firestoreSnapshotToArray(snapshot);
        const normalized = list.map((item) => normalizeUser(item.id, item));
        setStudents(normalized);
        setLoading(false);
      }, (err) => { console.error('Users load error:', err); setLoading(false); });
      unsubscribes.push(unsubUsers);

      const unsubGroups = onSnapshot(collection(db, 'groups'), (snapshot) => {
        setGroups(firestoreSnapshotToArray(snapshot));
      }, (err) => console.error('Groups load error:', err));
      unsubscribes.push(unsubGroups);
    } else if (activeMenu === 'schedule') {
      // Schedule Page only needs teachers
      const qTeachers = query(
        collection(db, 'users'),
        where('role', 'in', ['teacher', 'admin'])
      );
      const unsubUsers = onSnapshot(qTeachers, (snapshot) => {
        const list = firestoreSnapshotToArray(snapshot);
        const normalized = list.map((item) => normalizeUser(item.id, item));
        setTeachers(normalized.filter((item) => item.role === 'teacher'));
        setLoading(false);
      }, (err) => { console.error('Users load error:', err); setLoading(false); });
      unsubscribes.push(unsubUsers);
    } else {
      setLoading(false);
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub && unsub());
    };
  }, [activeMenu, currentYear]);

  useEffect(() => {
    const handleClickOutsideFooterCalendar = (event) => {
      if (footerCalendarRef.current && !footerCalendarRef.current.contains(event.target)) {
        setShowFooterCalendar(false);
      }
    };

    const handleEscapeFooterCalendar = (event) => {
      if (event.key === 'Escape') setShowFooterCalendar(false);
    };

    document.addEventListener('mousedown', handleClickOutsideFooterCalendar);
    document.addEventListener('touchstart', handleClickOutsideFooterCalendar);
    document.addEventListener('keydown', handleEscapeFooterCalendar);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFooterCalendar);
      document.removeEventListener('touchstart', handleClickOutsideFooterCalendar);
      document.removeEventListener('keydown', handleEscapeFooterCalendar);
    };
  }, []);

  const handleDelete = async (type, id) => {
    if (!id) return;

    const config = {
      user: { collectionName: 'users', confirm: t.deleteUserConfirm, log: t.actionUserDeleted },
      teacher: { collectionName: 'users', confirm: t.deleteUserConfirm, log: t.actionUserDeleted },
      student: { collectionName: 'users', confirm: t.deleteUserConfirm, log: t.actionUserDeleted },
      group: { collectionName: 'groups', confirm: t.deleteGroupConfirm, log: t.actionGroupDeleted },
      program: { collectionName: 'programs', confirm: t.deleteProgramConfirm, log: t.actionProgramDeleted },
      document: { collectionName: 'documents', confirm: t.deleteDocumentConfirm, log: t.actionDocumentDeleted },
      report: { collectionName: 'reports', confirm: t.deleteReportConfirm, log: t.actionReportDeleted },
      practice: { collectionName: 'practices', confirm: t.deletePracticeConfirm, log: t.actionPracticeDeleted },
      achievement: { collectionName: 'achievements', confirm: t.deleteAchievementConfirm, log: t.actionAchievementDeleted }
    }[type];

    if (!config) return;
    if (!window.confirm(config.confirm)) return;

    await deleteDocumentData(config.collectionName, id);
    await addActivityLog({ icon: '🗑️', text: config.log, type });

    if (['plans', 'programs', 'documents', 'reports', 'practices', 'achievements'].includes(activeMenu)) {
      await fetchTabData(activeMenu);
    }

    await loadAllData(true);
  };

  const renderSettings = () => (
    <div className="settings-page admin-section-card">
      <div className="section-header">
        <div>
          <h2>{t.settingsTitle}</h2>
          <p>{t.settingsSubtitle}</p>
        </div>
      </div>

      <div className="settings-grid">
        <div className="settings-card">
          <h3>{t.interfaceLanguage}</h3>
          <div className="settings-actions-row">
            {['kg', 'ru', 'en'].map((lang) => (
              <button key={lang} type="button" className={`btn-secondary ${language === lang ? 'active' : ''}`} onClick={() => handleLanguageChange(lang)}>
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeMenu === 'profile') {
  return (
    <AdminDashboardContent
      activeMenu="profile"
      setActiveMenu={setActiveMenu}
      adminProfile={adminProfile}
      onSaveAdminProfile={updateAdminProfile}
      language={language}
      t={t}
    />
  );
}
    if (activeMenu === 'schedule') {
      return (
        <SchedulePage
          mode="admin"
          role="admin"
          language={language}
          t={t}
          currentYear={currentYear}
          teachers={teachers}
          groups={groups}
          students={students}
          disciplines={disciplines}
        />
      );
    }
    if (activeMenu === 'plans') return <CurriculumPlanPage mode="admin" language={language} t={t} currentYear={currentYear} />;
    if (activeMenu === 'settings') return renderSettings();

    return (
      <AdminDashboardContent
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        openSection={openSection}
        stats={stats}
        registeredUsers={registeredUsers}
        users={users}
        teachers={teachers}
        students={students}
        groups={groups}
        disciplines={disciplines}
        plans={plans}
        programs={programs}
        documents={documents}
        reports={reports}
        practices={practices}
        achievements={achievements}
        activityData={activityData}
        recentActions={recentActions}
        setShowAllActionsModal={setShowAllActionsModal}
        adminProfile={adminProfile}
        onSaveAdminProfile={updateAdminProfile}
        currentYear={currentYear}
        language={language}
        t={t}
        handleDelete={handleDelete}
        onSaveUsers={saveUsers}
        onCreateTeacher={(data) => addUser({ ...data, role: normalizePositionToRole(data.position, data.role || 'teacher') })}
        onSaveTeachers={saveTeachers}
        onCreateStudent={(data) => addUser({ ...data, role: 'student', canAccessPortal: false })}
        onSaveStudents={saveStudents}
        onPromoteStudents={promoteStudentsToNextCourse}
        onCreateGroup={addGroup}
        onSaveGroups={saveGroups}
        onCreateProgram={addProgram}
        onSavePrograms={savePrograms}
        onCreateDocument={addDocument}
        onSaveDocuments={saveDocuments}
        onCreateReport={addReport}
        onSaveReports={saveReports}
        onCreatePractice={addPractice}
        onSavePractices={savePractices}
        onCreateAchievement={addAchievement}
        onSaveAchievements={saveAchievements}
        addNotificationToAdmin={addNotificationToAdmin}
        addNotificationToTeacher={addNotificationToTeacher}
        addNotificationToAllTeachers={addNotificationToAllTeachers}
        addActivityLog={addActivityLog}
      />
    );
  };

  return (
    <div className="admin-dashboard top-nav-layout">
      <AdminSidebar
        activeMenu={activeMenu}
        setActiveMenu={(menu) => {
          openSection(menu);
          setIsMobileMenuOpen(false);
        }}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        t={t}
      />

      <main className="admin-main">
        <AdminTopbar
          onLogout={logout}
          adminProfile={adminProfile}
          adminName={adminProfile.fullName || adminProfile.name}
          adminPosition={adminProfile.position}
          adminEmail={adminProfile.email}
          adminPhone={adminProfile.phone}
          adminPhoto={adminProfile.photoUrl || adminProfile.avatar}
          notifications={adminNotifications}
          unreadCount={unreadCount}
          onMarkRead={markNotificationRead}
          onMarkAllRead={markAllNotificationsAsRead}
          setActiveMenu={setActiveMenu}
          onToggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
          
          language={language}
          onLanguageChange={handleLanguageChange}
          t={t}
        />


        <div 
          className="admin-content"
          style={['plans', 'schedule'].includes(activeMenu) ? { overflow: 'hidden' } : {}}
        >
          {loading && activeMenu !== 'dashboard' ? <Loader text={t.loading} size={50} /> : renderContent()}
        </div>


        {activeMenu === 'dashboard' && (
          <footer className="admin-footer">
            <span>{t.footer}</span>

            <div className="footer-calendar" ref={footerCalendarRef}>
              <button type="button" onClick={() => setShowFooterCalendar((prev) => !prev)}>
                <CalendarDays size={16} />
                {t.academicCalendar}
              </button>

              {showFooterCalendar && (
                <div className="admin-footer-calendar-card">
                  <div className="admin-calendar-head">
                    <div>
                      <h3>{t.calendar || t.academicCalendar || 'Календарь'}</h3>
                    </div>
                    <button
                      type="button"
                      className="admin-calendar-close"
                      onClick={() => setShowFooterCalendar(false)}
                    >
                      <X size={20} strokeWidth={2.4} />
                    </button>
                  </div>

                  <div className="admin-calendar-divider" />

                  <DatePicker
                    selected={footerSelectedDate}
                    onChange={(date) => {
                      setFooterSelectedDate(date);
                      setShowFooterCalendar(false);
                    }}
                    inline
                    locale="ru"
                    calendarStartDay={1}
                    dateFormat="dd.MM.yyyy"
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth, changeMonth, changeYear }) => (
                      <div className="admin-datepicker-custom-head">
                        <button type="button" className="admin-datepicker-arrow" onClick={decreaseMonth}>
                          ‹
                        </button>
                        <div className="admin-datepicker-center">
                          <strong>{getMonthTitle(date)}</strong>
                          <div className="admin-datepicker-selects">
                            <select
                              value={date.getMonth()}
                              onChange={(event) => changeMonth(Number(event.target.value))}
                            >
                              {(monthNames[language] || monthNames.ru).map((month, index) => (
                                <option key={month} value={index}>
                                  {month}
                                </option>
                              ))}
                            </select>
                            <select
                              value={date.getFullYear()}
                              onChange={(event) => changeYear(Number(event.target.value))}
                            >
                              {Array.from({ length: 14 }, (_, i) => new Date().getFullYear() - 6 + i).map((year) => (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button type="button" className="admin-datepicker-arrow" onClick={increaseMonth}>
                          ›
                        </button>
                      </div>
                    )}
                  />

                  <div className="admin-calendar-selected">
                    <span>{t.selectedDate}:</span>
                    <strong>{formatDate(footerSelectedDate)}</strong>
                  </div>
                </div>
              )}
            </div>
          </footer>
        )}
      </main>


      {showAllActionsModal && (
        <RecentActionsModal t={t} actions={recentActions} onClose={() => setShowAllActionsModal(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;